import { withRouter } from 'next/router';
import { number, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { Query } from 'react-apollo';
import qs from 'qs';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorPage from 'components/ErrorPage';
import { registerBundlesLoaded } from 'util/analytics';
import { getErrorCodeFromMessage } from 'util/error';
import { getCleanPath, toUrl, getPostIdFromUrl } from 'util/link';
import { formatWrappedComponentDisplayName } from 'util/hoc';
import { extractMetadataFromBundleItems } from 'util/metadata';
import { addCustomPostProps } from 'util/posts';
import PAGE_QUERY from 'graphql/page';
import FETCH_MORE_QUERY from 'graphql/fetchMoreContent';
import { SITE_KEY, SITE_ORIGIN } from 'constants/site';

const _clientPageErrors = new Set();

// Extract the canonical url from page bundles
const getCanonicalUrl = bundles => {
  const metadataBundle = bundles.find(b => b.layout === 'metadata');
  let url;
  if (metadataBundle) {
    const metadata = extractMetadataFromBundleItems(metadataBundle.items);
    // use 'gen-canonical' which is the actual canonical URL for the post to
    // handle redirects. 'canonical' is usually equivalent to `gen-canonical`,
    // but could be overridden by the editor via the 'canonical' WP ACF field.
    //
    // 'gen-canonical' is new, so we want to fallback to 'canonical' if
    // `gen-canonical` doesn't exist.
    const canonical = metadata['gen-canonical'] || metadata.canonical;
    url = toUrl(canonical);
  }
  return url;
};

// Return a redirect url if pathWithoutParams does not match the canonical url
// in the metadata bundle.
export const getCanonicalRedirect = (bundles, pathWithoutParams, params) => {
  // Get the canonical URL using the metadata from Flow
  const canonicalUrl = getCanonicalUrl(bundles);
  let redirect = '';

  if (!canonicalUrl) {
    return redirect;
  }

  const {
    origin: canonicalOrigin,
    pathname: canonicalPathname,
    href: canonicalHref
  } = canonicalUrl;

  const isPreview = params.preview === 'true';
  const hasWithSlug = canonicalUrl.searchParams.get('with_slug') === 'true';

  if (canonicalOrigin !== SITE_ORIGIN) {
    // Wrong site
    redirect = canonicalHref;
  } else if (canonicalPathname !== pathWithoutParams) {
    // Redirect if:
    // - The canonical URL has `with_slug=true`
    // - Or, if preview is false
    if (hasWithSlug || !isPreview) {
      // Wrong pathname
      redirect = canonicalPathname;
    }
  }

  if (redirect) {
    // Preserve params on redirect
    const query = qs.stringify(params);
    redirect = `${redirect}${query ? `?${query}` : ''}`;
  }

  return redirect;
};

export default (
  WrappedComponent,
  { type, limit = 4, fetchMoreLimit = 4, ErrorPageComponent = ErrorPage }
) => {
  class withPageQuery extends Component {
    static displayName = formatWrappedComponentDisplayName(
      'withPageQuery',
      WrappedComponent
    );

    static propTypes = {
      router: shape({
        asPath: string,
        pathname: string
      }).isRequired,
      ctx: object, // eslint-disable-line react/forbid-prop-types
      statusCode: number
    };

    static defaultProps = {
      ctx: undefined,
      statusCode: undefined
    };

    static handleUpdateQuery(prev, { fetchMoreResult }) {
      if (!fetchMoreResult) {
        return prev;
      }
      const prevContent = prev.page.contentBundles;
      const nextContent = fetchMoreResult.page.contentBundles;

      if (!nextContent || !nextContent.data || nextContent.data.length === 0) {
        // Protect against the unlikely case that the query doesn't return data
        // for the next cursor. This is probably a bug with page bundler, since
        // we should only receive a next cursor if there is actually more
        // content to fetch.
        return {
          page: {
            ...prev.page,
            // Update content bundles with new data
            contentBundles: {
              ...prevContent,
              pageInfo: {
                __typename: prevContent.pageInfo.__typename,
                nextCursor: null
              }
            }
          }
        };
      }
      // Register bundle load event
      registerBundlesLoaded(nextContent.data.length);
      // Update Apollo store
      return {
        page: {
          ...prev.page,
          // Update content bundles with new data
          contentBundles: {
            __typename: prevContent.__typename,
            pageInfo: {
              ...prevContent.pageInfo,
              ...nextContent.pageInfo
            },
            data: [...prevContent.data, ...nextContent.data]
          }
        }
      };
    }

    constructor(props) {
      super(props);
      this.state = {
        isLoadingMore: false
      };
    }

    render() {
      const { router, ctx, statusCode, ...props } = this.props;
      const isBrowser = typeof window !== 'undefined';

      // remove query params from router's asPath  because we don't need/use the
      // query string or fragment id in the graphql query
      const pathWithoutParams = getCleanPath(router.asPath);

      const variables = {
        // Include query params, like preview and cache
        ...router.query,
        // Note, order is important since the router.query _could_ include `path`
        path: pathWithoutParams,
        site: SITE_KEY,
        limit
      };

      // Save errors on the client and catch on the server
      const errorPolicy = isBrowser ? 'all' : 'none';

      // set a header for fastly to pick up for logging. note that we're
      // using 0 here as the default value because we'll be using an
      // integer field in datadog (so we can do ranges) so we always
      // need the header to have an integer value.
      if (ctx && ctx.res) {
        ctx.res.header('X-PostID', getPostIdFromUrl(pathWithoutParams) || '0');
      }

      // Bail early if an error occurred during initial server side render (i.e.
      // getDataFromTree). Apollo does not store the error for hydration, so
      // we must bail here to prevent the <Query> component from rendering and
      // later refetching client side.
      if (statusCode >= 400) {
        return (
          <ErrorPageComponent query={router.query} statusCode={statusCode} />
        );
      }

      return (
        <ErrorBoundary>
          <Query
            query={PAGE_QUERY}
            context={{
              // Set a HIGH timeout value when `preview=true` otherwise use
              // global value
              ...(router.query.preview === 'true' ? { timeout: 60000 } : {})
            }}
            variables={variables}
            errorPolicy={errorPolicy}
            notifyOnNetworkStatusChange
          >
            {({ data, loading: isLoading, error, fetchMore }) => {
              // Handle client side errors. These must be handled separately
              // to server errors due to limitations with Apollo's store.
              if (error) {
                // Track errors. The error prop is the most recent query error.
                // It might not be the error that occurred for this (cached)
                // query.
                if (error.graphQLErrors) {
                  _clientPageErrors.add(
                    ...error.graphQLErrors.map(e => ({
                      path: e && e.data ? e.data.path : pathWithoutParams,
                      statusCode:
                        e && e.data
                          ? getErrorCodeFromMessage(e.data.message)
                          : 500
                    }))
                  );
                }

                if (error.networkError) {
                  return (
                    <ErrorPageComponent query={router.query} statusCode={500} />
                  );
                }
              }
              // Check for errors that were returned for this path's query.
              // E.g. If the reader has navigates back to this route (client side)
              // the error prop and data will be empty, so we need to get the
              // error from the client scoped var.
              const pageError = [..._clientPageErrors].find(
                e => e && e.path === pathWithoutParams
              );

              if (pageError) {
                // A known error has occurred
                return (
                  <ErrorPageComponent
                    query={router.query}
                    statusCode={pageError.statusCode}
                  />
                );
              }

              if (isLoading === false && data.page) {
                // Data has been fetched from Flow. Force the canonical resource
                // by redirecting if necessary.
                const { id, ...params } = router.query;
                const redirectTo = getCanonicalRedirect(
                  data.page.pageBundles,
                  pathWithoutParams,
                  params
                );

                if (redirectTo) {
                  if (typeof window === 'undefined') {
                    // Server
                    if (ctx) {
                      ctx.res.redirect(301, redirectTo);
                    }
                  } else {
                    // Browser
                    window.location.href = redirectTo;
                  }
                  // Don't render anything if this page will be redirected
                  return null;
                }
              }

              // Note, Apollo does not reset data when the query's variables
              // change. This means the data prop will be the previous query's
              // value while the new query is loading. If we are loading more,
              // and it's not an infinite scroll request, we should reset the
              // data to trigger proper loading states in our components.
              // See: https://github.com/apollographql/react-apollo/issues/2202
              const shouldResetData = isLoading && !this.state.isLoadingMore;

              const pageBundles = shouldResetData
                ? undefined
                : data && data.page && data.page.pageBundles;

              const contentBundles = shouldResetData
                ? undefined
                : data && data.page && data.page.contentBundles;

              const onLoadMore = () => {
                this.setState({ isLoadingMore: true }, () =>
                  fetchMore({
                    query: FETCH_MORE_QUERY,
                    variables: {
                      ...variables,
                      cursor: contentBundles.pageInfo.nextCursor,
                      limit: fetchMoreLimit
                    },
                    updateQuery: withPageQuery.handleUpdateQuery
                  })
                    .then(() => {
                      this.setState({ isLoadingMore: false });
                    })
                    .catch(() => {
                      // TODO: handle fetch error
                    })
                );
              };

              // More content is available if a nextCursor is set
              const hasMore = !!(
                contentBundles && contentBundles.pageInfo.nextCursor
              );

              // CLEANUP: is there a better place to handle this?
              let parsedContentBundles;
              if (contentBundles && contentBundles.data) {
                // Add custom post props to post bundles
                parsedContentBundles = contentBundles.data.map(b =>
                  b.layout === 'post' ? addCustomPostProps(b) : b
                );
              }

              return (
                <WrappedComponent
                  pathWithoutParams={pathWithoutParams}
                  pageBundles={pageBundles}
                  pageType={type}
                  contentBundles={parsedContentBundles}
                  onLoadMore={onLoadMore}
                  isLoadingMore={isLoading || this.state.isLoadingMore}
                  hasMore={hasMore}
                  {...props}
                  // TODO: set httpStatus (will require some refactoring)
                />
              );
            }}
          </Query>
        </ErrorBoundary>
      );
    }
  }
  return withRouter(withPageQuery);
};
